import { withZod } from '@remix-validated-form/with-zod'
import type { Feature, GeoJsonProperties, Geometry } from 'geojson'
import { z } from 'zod'
import { zfd } from 'zod-form-data'
import { getFindFromFilters, type MapFiltersType } from '~/atoms'
import { type ModelOrganization } from './organization'
import {
  LocationFilter,
  LocationFilterSchema,
  MapFiltersSchema,
} from './shared'
import { type ModelUser } from './user'

const notificationFrequency = ['never', 'update', 'hourly'] as const
export const notificationFrequencyLabels = {
  never: 'Notify Off',
  update: 'Update Email',
  hourly: 'Hourly Email',
}
export type SearchEmailNotificationFrequency =
  (typeof notificationFrequency)[number]

export type Search = {
  _id: string
  tid: string
  created: string
  modified: string
  organization: ModelOrganization
  user: ModelUser
  slug: string
  name: string
  description?: string

  listingsCount: number
  lat: number
  lng: number
  z: number
  filters: MapFiltersType
  location: LocationFilter

  notify?: boolean | string
  notificationFrequency?: SearchEmailNotificationFrequency
}

export const SearchSchema = z.object({
  _id: z.string(),
  tid: z.string(),
  created: z.string(),
  modified: z.string(),
  organization: z.custom<ModelOrganization>(),
  user: z.custom<ModelUser>(),
  name: zfd.text(z.string().min(1, 'Enter a name for this search')),
  slug: zfd.text(z.string()),
  description: zfd.text(z.string().optional()),
  listingsCount: zfd.numeric(z.number()),
  lat: zfd.numeric(z.number()),
  lng: zfd.numeric(z.number()),
  z: zfd.numeric(z.number()),
  filters: MapFiltersSchema,
  location: LocationFilterSchema,

  notify: zfd.checkbox().optional(),
  notificationFrequency: z.enum(notificationFrequency).optional(),
}) satisfies z.ZodType<Search>

export const searchValidator = withZod(SearchSchema)

export function getFindFromSearch(search: Search) {
  const find: { [key: string]: any } = {}

  if (search.location) {
    find.location = search.location
  }
  if (search.filters) {
    return {
      ...find,
      ...getFindFromFilters(search.filters),
    }
  }
  return find
}

export function transformProperties(
  properties: LocationFilter['$geoWithin']['$geometry']['properties']
) {
  if (!properties) return {}

  return {
    ...properties,
    isCircle: properties.isCircle === 'true',
  }
}

export function locationToFeature(
  location: LocationFilter['$geoWithin']['$geometry']
): Feature<Geometry, GeoJsonProperties> {
  const feature: Feature<Geometry, GeoJsonProperties> = {
    type: 'Feature',
    properties: transformProperties(location.properties),
    geometry: {
      coordinates: location.coordinates,
      type: 'Polygon',
    },
  }

  return feature
}
